/**
 * Tela dos orçamentos.
 *
 * Papel: junta o resumo do que está em aberto e do que foi aprovado, o filtro
 * por situação, a tabela das propostas e a confirmação de exclusão. O
 * formulário mora em endereço próprio — `/orcamentos/novo` e
 * `/orcamentos/:id` —, aberto pela `PaginaFormularioOrcamento`.
 *
 * Como usar: quem tem nível `ver` não recebe `onAbrir` nem `onRemover`, e a
 * tabela deixa de responder ao clique — continua legível, e traz o que decide
 * alguma coisa: número, data, casa, fornecedor, status e total.
 *
 * A exclusão recusada pela API mostra a mensagem dentro da caixa de
 * confirmação, que continua aberta — fora dela, a frase ficaria atrás do véu.
 *
 * O resumo soma `totalDoOrcamento` sobre as propostas aprovadas, e é a única
 * leitura da tela que diz quanto a família se comprometeu a gastar — as demais
 * situações somam propostas que ainda podem não virar despesa nenhuma.
 */
import {
  type Orcamento,
  type StatusOrcamento,
  STATUS_ORCAMENTO,
  acessoAlcanca,
  totalDoOrcamento,
} from '@concierge/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { formatarDinheiro } from '@/shared/lib/formato';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Chip } from '@/shared/ui/chip';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { TabelaOrcamentos } from '../components/tabela-orcamentos';
import { useExcluirOrcamento, useOrcamentos } from '../hooks/usar-orcamentos';
import { ROTULOS_STATUS } from '../rotulos';

/** O recorte que o filtro aplica, com `todos` para não recortar nada. */
type Recorte = StatusOrcamento | 'todos';

/**
 * Página dos orçamentos.
 *
 * @returns A tela com o resumo, o filtro, a tabela e a confirmação de exclusão.
 */
export function PaginaOrcamentos() {
  const navigate = useNavigate();
  const [aRemover, setARemover] = useState<Orcamento | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const [recorte, setRecorte] = useState<Recorte>('todos');

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'orcamentos', 'editar');

  const consultaOrcamentos = useOrcamentos();
  const excluir = useExcluirOrcamento();

  const orcamentos = consultaOrcamentos.data ?? [];

  const mostrados =
    recorte === 'todos'
      ? orcamentos
      : orcamentos.filter((orcamento) => orcamento.status === recorte);

  const emAberto = orcamentos.filter((orcamento) => orcamento.status === 'orcamento');
  const aprovados = orcamentos.filter((orcamento) => orcamento.status === 'aprovado');
  const somaAprovada = aprovados.reduce(
    (total, orcamento) => total + totalDoOrcamento(orcamento.itens, orcamento.desconto),
    0,
  );

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(orcamento: Orcamento) {
    setErroDaRemocao(null);
    setARemover(orcamento);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o orçamento confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      await excluir.mutateAsync(aRemover.id);
      setARemover(null);
    } catch (falha) {
      setErroDaRemocao(
        falha instanceof ErroDaApi
          ? falha.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <div>
      <CabecalhoDoModulo titulo="Orçamentos" />

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-border bg-card p-4">
          <span className="font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
            Em orçamento
          </span>
          <span className="tabular mt-1 block text-lg text-foreground">{emAberto.length}</span>
        </div>
        <div className="rounded-md border border-border bg-card p-4">
          <span className="font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
            Aprovados
          </span>
          <span className="tabular mt-1 block text-lg text-foreground">
            {aprovados.length}
          </span>
        </div>
        <div className="rounded-md border border-border bg-card p-4">
          <span className="font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
            Valor aprovado
          </span>
          <span className="tabular mt-1 block text-lg text-foreground">
            {formatarDinheiro(somaAprovada)}
          </span>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Chip ativo={recorte === 'todos'} onClick={() => setRecorte('todos')}>
          Todos
        </Chip>
        {STATUS_ORCAMENTO.map((status) => (
          <Chip key={status} ativo={recorte === status} onClick={() => setRecorte(status)}>
            {ROTULOS_STATUS[status]}
          </Chip>
        ))}
      </div>

      <SecaoCadastro
        titulo="Propostas"
        rotuloNovo="+ Novo orçamento"
        onNovo={podeEditar ? () => navigate('/orcamentos/novo') : undefined}
        carregando={consultaOrcamentos.isPending}
        erro={consultaOrcamentos.error}
        onTentarDeNovo={() => void consultaOrcamentos.refetch()}
        vazia={mostrados.length === 0}
        mensagemVazia={
          recorte === 'todos'
            ? 'Nenhum orçamento cadastrado ainda.'
            : 'Nenhum orçamento nesta situação.'
        }
      >
        <TabelaOrcamentos
          orcamentos={mostrados}
          onAbrir={
            podeEditar ? (orcamento) => navigate(`/orcamentos/${orcamento.id}`) : undefined
          }
          onRemover={podeEditar ? handlePedirRemocao : undefined}
        />
      </SecaoCadastro>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o orçamento {aRemover?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>
              Os itens da proposta vão junto, com as imagens deles. A ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evento) => {
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
              disabled={excluir.isPending}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
